import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import { Campaign, Conversion, ConversionValidatePayload, PaginatedResponse } from "@/types/api";

export interface ConversionPreviewResponse {
  short_token: string;
  remuneracao_modelo: string;
  remuneracao_valor: string;
  desconto_pct: string | null;
}

export const useCampaigns = (page = 1, pageSize = 10) => {
  return useQuery({
    queryKey: ["campaigns", { page, pageSize }],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Campaign>>("/campaigns/", {
        params: { page, page_size: pageSize },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });
};

export const useCampaign = (id: string | undefined) => {
  return useQuery({
    queryKey: ["campaigns", id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get<Campaign>(`/campaigns/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

export const useCampaignConversions = (id: string | undefined, page = 1, pageSize = 10) => {
  return useQuery({
    queryKey: ["campaigns", id, "conversions", { page, pageSize }],
    queryFn: async () => {
      if (!id) return { items: [], total: 0, page: 1, page_size: 10, pages: 0 };
      const { data } = await api.get<PaginatedResponse<Conversion>>(
        `/campaigns/${id}/conversions`,
        { params: { page, page_size: pageSize } }
      );
      return data;
    },
    enabled: !!id,
    placeholderData: (prev) => prev,
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newCampaign: Omit<Campaign, "id" | "status" | "created_at">) => {
      const { data } = await api.post<Campaign>("/campaigns/", newCampaign);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
};

export const useAcceptCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Campaign>(`/campaigns/${id}/accept`);
      return data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns", id] });
    },
  });
};

export const useDeclineCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/campaigns/${id}/decline`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
};

export const useCloseCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/campaigns/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
};

export const useValidateConversion = () => {
  return useMutation({
    mutationFn: async (payload: ConversionValidatePayload) => {
      const { data } = await api.post<Conversion>("/conversions/validate", payload);
      return data;
    },
  });
};

export const useConversionPreview = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await api.get<ConversionPreviewResponse>(
        `/conversions/preview?token=${encodeURIComponent(token)}`
      );
      return data;
    },
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/campaigns/${id}/permanent`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
};

export const useCampaignsByCompany = (companyId: number | undefined) => {
  return useQuery({
    queryKey: ["campaigns", "byCompany", companyId],
    queryFn: async () => {
      const { data } = await api.get<PaginatedResponse<Campaign>>("/campaigns/", {
        params: { page: 1, page_size: 100 },
      });
      const items = data?.items ?? [];
      return items.filter((c) => c.company_id === companyId);
    },
    enabled: !!companyId,
  });
};
